import { IntlMessageFormat } from 'intl-messageformat'
import { formatMatchingEventDate, formatCents } from 'betterplace-utils'
import type { MatchingEventBannerData } from '../../types'
export type GetPlatformEventTranslator = (
  matchingEvent: Pick<
    MatchingEventBannerData,
    'actor' | 'matchingPercentage' | 'hasOnlyOneProject' | 'providedAmountInCents'
  >,
  locale: string
) => string
export type GetEventTimeTranslator = (
  matchingEvent: Pick<MatchingEventBannerData, 'startsAt' | 'endsAt'> & { isSingleDayEvent: boolean },
  locale: string
) => string
const EVENT_TIME_TEMPLATES = {
  en: '{startsAt}{isSingleDayEvent, select, true {} other { to {endsAt}}}',
  de: '{startsAt}{isSingleDayEvent, select, true {} other { bis {endsAt}}}',
} as const
const PLATFORM_EVENT_INFO_TEMPLATES = {
  en: '{actor} {percentage, select, 100 {doubles} 200 {triples} 300 {quadruples} other {matches {percentage} % to}} every single donation to {hasOnlyOneProject, select, true {the participating project} other {participating projects}} until the budget of {budget} is used up. ',
  de: '{actor} {percentage, select, 100 {verdoppelt} 200 {verdreifacht} 300 {vervierfacht} other {verstärkt}} jede Einzelspende {hasOnlyOneProject, select, true {für das teilnehmende Projekt} other {für teilnehmende Projekte}}{percentage, select, 100 {} 200 {} 300 {} other { mit {percentage} %}}, bis das Budget von {budget} aufgebraucht ist. ',
} as const

function resolveTemplateLocale<L extends string>(locale: string, templates: Record<L, string>): L {
  if (locale in templates) return locale as L
  return 'de' as L
}

export const getEventTimeText: GetEventTimeTranslator = (
  { startsAt: startsAt_, endsAt: endsAt_, isSingleDayEvent },
  locale
) => {
  const resolvedLocale = resolveTemplateLocale(locale, EVENT_TIME_TEMPLATES)
  const template = EVENT_TIME_TEMPLATES[resolvedLocale]
  const startsAt = formatMatchingEventDate(startsAt_, resolvedLocale)
  const endsAt = formatMatchingEventDate(endsAt_, resolvedLocale)
  const messageFormat = new IntlMessageFormat(template, resolvedLocale)
  return String(
    messageFormat.format({
      startsAt,
      endsAt,
      isSingleDayEvent,
    })
  )
}

export const getPlatformEventInfoText: GetPlatformEventTranslator = (
  { actor, matchingPercentage, hasOnlyOneProject, providedAmountInCents },
  locale
) => {
  const resolvedLocale = resolveTemplateLocale(locale, PLATFORM_EVENT_INFO_TEMPLATES)
  const template = PLATFORM_EVENT_INFO_TEMPLATES[resolvedLocale]
  const budget = formatCents(resolvedLocale, providedAmountInCents)
  const messageFormat = new IntlMessageFormat(template, resolvedLocale)
  return String(
    messageFormat.format({
      actor: actor ?? '',
      percentage: matchingPercentage ?? 0,
      hasOnlyOneProject: Boolean(hasOnlyOneProject),
      budget,
    })
  )
}
